<template>
  <div class="page-container">
    <PageHeader title="报告包管理">
      <template #actions>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="handleAdd">新增报告包</el-button>
      </template>
    </PageHeader>

    <SearchPanel>
      <el-select v-model="query.rrmCustomer" clearable placeholder="客户群体" size="small" @change="handleSearchCustomerChange">
        <el-option v-for="item in searchCustomerOptions" :key="item" :label="item" :value="item"></el-option>
      </el-select>
      <el-select v-model="query.rrmProvince" clearable placeholder="省份" size="small" :disabled="!query.rrmCustomer" @change="handleSearchProvinceChange">
        <el-option v-for="item in searchProvinceOptions" :key="item" :label="item" :value="item"></el-option>
      </el-select>
      <el-select v-model="query.rrmCity" clearable placeholder="地市" size="small" :disabled="!query.rrmProvince" @change="handleSearchCityChange">
        <el-option v-for="item in searchCityOptions" :key="item" :label="item" :value="item"></el-option>
      </el-select>
      <el-select v-model="query.rrmCourseType" clearable placeholder="课程类型" size="small" :disabled="!query.rrmCity" @change="handleSearch">
        <el-option v-for="item in searchCourseTypeOptions" :key="item" :label="item" :value="item"></el-option>
      </el-select>
      <el-input v-model="query.relationRrmfIdOneName" clearable placeholder="考勤成绩模板" size="small" style="width: 150px" @keyup.enter.native="handleSearch" />
      <el-input v-model="query.relationRrmfIdTwoName" clearable placeholder="评估分数模板" size="small" style="width: 150px" @keyup.enter.native="handleSearch" />
      <el-input v-model="query.relationRrmfIdThreeName" clearable placeholder="总结报告模板" size="small" style="width: 150px" @keyup.enter.native="handleSearch" />
      <template #actions>
        <el-button size="small" @click="resetQuery">重置</el-button>
        <el-button type="primary" size="small" @click="handleSearch">查询</el-button>
      </template>
    </SearchPanel>

    <TablePanel>
      <aj-table
        v-loading="loading"
        :data="list"
        :columns="columns"
        :current-page.sync="query.currentPage"
        :page-size.sync="query.pageSize"
        :page-sizes="[10, 20, 30, 50]"
        :total="total"
        row-id-key="rrmId"
        stripe
        use-internal-pagination
        @page-change="handlePageChange"
        @operate-btn-click="handleOperateBtnClick"
      ></aj-table>
      <EmptyState v-if="!loading && list.length === 0" title="暂无报告包" icon="el-icon-files"></EmptyState>
    </TablePanel>

    <!-- 新增弹窗 -->
    <aj-form-add
      :dialog-visible.sync="addDialogVisible"
      :form-config="addFormConfig"
      :form-data="addFormData"
      dialog-title="新增报告包"
      label-width="100px"
      :confirm-loading="submitLoading"
      @confirm="handleAddSubmit"
    />

    <!-- 编辑弹窗 -->
    <aj-form-edit
      :dialog-visible.sync="editDialogVisible"
      :form-config="editFormConfig"
      :form-data="editFormData"
      dialog-title="编辑报告包"
      label-width="100px"
      :confirm-loading="submitLoading"
      @confirm="handleEditSubmit"
    />

    <!-- 查看弹窗 -->
    <el-dialog v-dialog-drag-resize :visible.sync="viewDialogVisible" width="80%" top="7vh" custom-class="detail-dialog" :close-on-click-modal="false" :show-close="false">
      <template #title>
        <div class="detail-dialog-header">
          <span class="detail-dialog-title">报告包详情</span>
          <i class="dialog-close-btn" @click="viewDialogVisible = false" />
        </div>
      </template>
      <div class="detail-content" v-if="viewData">
        <!-- 分区标题：基本信息 -->
        <div class="detail-section-title">
          <i class="el-icon-office-building"></i>
          <span>基本信息</span>
        </div>
        <!-- 两列布局：基本信息 -->
        <div class="detail-table detail-table--2col">
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">客户群体</div>
            <div class="detail-cell detail-cell--value">{{ viewData.rrmCustomer || '-' }}</div>
            <div class="detail-cell detail-cell--label">省份</div>
            <div class="detail-cell detail-cell--value">{{ viewData.rrmProvince || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">地市</div>
            <div class="detail-cell detail-cell--value">{{ viewData.rrmCity || '-' }}</div>
            <div class="detail-cell detail-cell--label">课程类型</div>
            <div class="detail-cell detail-cell--value">{{ viewData.rrmCourseType || '-' }}</div>
          </div>
        </div>

        <!-- 分区标题：资料信息 -->
        <div class="detail-section-title">
          <i class="el-icon-folder-opened"></i>
          <span>资料信息</span>
        </div>

        <!-- 关联模板信息 -->
        <div class="detail-table">
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">考勤成绩模板</div>
            <div class="detail-cell detail-cell--value">
              <div>{{ viewData.relationRrmfIdOneName || '-' }}</div>
              <template v-if="viewData.rtReportModelFileOne">
                <div v-if="viewData.rtReportModelFileOne.rrmfDes" class="template-des">备注：{{ viewData.rtReportModelFileOne.rrmfDes }}</div>
                <div v-if="viewData.rtReportModelFileOne.rrmfImages" class="template-img-wrap">
                  <el-image :src="viewData.rtReportModelFileOne.rrmfImages" fit="contain" :preview-src-list="[viewData.rtReportModelFileOne.rrmfImages]" style="width: 160px; height: 100px; border-radius: 6px; border: 1px solid #e2e8f0;" />
                </div>
              </template>
            </div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">评估分数模板</div>
            <div class="detail-cell detail-cell--value">
              <div>{{ viewData.relationRrmfIdTwoName || '-' }}</div>
              <template v-if="viewData.rtReportModelFileTwo">
                <div v-if="viewData.rtReportModelFileTwo.rrmfDes" class="template-des">备注：{{ viewData.rtReportModelFileTwo.rrmfDes }}</div>
                <div v-if="viewData.rtReportModelFileTwo.rrmfImages" class="template-img-wrap">
                  <el-image :src="viewData.rtReportModelFileTwo.rrmfImages" fit="contain" :preview-src-list="[viewData.rtReportModelFileTwo.rrmfImages]" style="width: 160px; height: 100px; border-radius: 6px; border: 1px solid #e2e8f0;" />
                </div>
              </template>
            </div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">总结报告模板</div>
            <div class="detail-cell detail-cell--value">
              <div>{{ viewData.relationRrmfIdThreeName || '-' }}</div>
              <template v-if="viewData.rtReportModelFileThree">
                <div v-if="viewData.rtReportModelFileThree.rrmfDes" class="template-des">备注：{{ viewData.rtReportModelFileThree.rrmfDes }}</div>
                <div v-if="viewData.rtReportModelFileThree.rrmfImages" class="template-img-wrap">
                  <el-image :src="viewData.rtReportModelFileThree.rrmfImages" fit="contain" :preview-src-list="[viewData.rtReportModelFileThree.rrmfImages]" style="width: 160px; height: 100px; border-radius: 6px; border: 1px solid #e2e8f0;" />
                </div>
              </template>
            </div>
          </div>
        </div>
        <!-- 单列布局：资料信息 -->
        <div class="detail-table">
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">包内含资料</div>
            <div class="detail-cell detail-cell--value">{{ viewData.rrmModelName || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">资料地址</div>
            <div class="detail-cell detail-cell--value">
              <div v-if="viewEditingPath" class="detail-path-edit">
                <el-upload
                  ref="pathUploadRef"
                  drag
                  action="#"
                  :auto-upload="false"
                  :limit="1"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar"
                  :on-change="handlePathFileChange"
                  :on-remove="handlePathFileRemove"
                  :on-exceed="() => $message.warning('只能上传一个文件')"
                  :before-upload="() => false"
                  :file-list="viewEditFileList"
                >
                  <i class="el-icon-upload"></i>
                  <div class="el-upload__text">将文件拖到此处，或<em>点击选择文件</em></div>
                </el-upload>
                <div class="detail-path-actions">
                  <el-button size="mini" @click="viewEditingPath = false">取消</el-button>
                  <el-button type="primary" size="mini" :loading="viewPathLoading" @click="handleSaveModelPath">保存</el-button>
                </div>
              </div>
              <div v-else class="detail-path-show">
                <div v-if="viewData.rrmModelPath" class="detail-file-list">
                  <span v-for="(url, idx) in viewData.rrmModelPath.split(',').filter(Boolean)" :key="idx" class="detail-file-chip detail-file-preview" @click="openFilePreview(url)">
                    <i class="el-icon-view"></i>
                    <span>{{ url.split('/').pop() }}</span>
                  </span>
                  <el-button type="text" size="mini" icon="el-icon-download" class="detail-download-btn" @click="downloadToLocal(viewData.rrmModelPath.split(',')[0])">保存到电脑</el-button>
                </div>
                <span v-else class="detail-empty">暂无资料</span>
                <el-button type="text" icon="el-icon-edit" class="detail-path-trigger" @click="handleEditModelPath">编辑</el-button>
              </div>
            </div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">本地附加</div>
            <div class="detail-cell detail-cell--value detail-cell--pre">{{ viewData.rrmAttachment || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">材料特殊性备注</div>
            <div class="detail-cell detail-cell--value detail-cell--pre">{{ viewData.rrmSpecial || '-' }}</div>
          </div>
        </div>

        <!-- 底部元信息 -->
        <div class="detail-meta">
          <span class="detail-meta__item"><i class="el-icon-user"></i> {{ viewData.createusername || '-' }}</span>
          <span class="detail-meta__item"><i class="el-icon-time"></i> {{ viewData.createtime || '-' }}</span>
        </div>
      </div>
      <template #footer>
        <el-button size="small" @click="viewDialogVisible = false">关 闭</el-button>
      </template>
    </el-dialog>

    <!-- 文件预览 -->
    <FilePreview :visible.sync="previewVisible" :url="previewFileUrl" />
  </div>
</template>

<script>
import {
  getRtReportModelList,
  getRtReportModelListParam,
  getRtReportModelDetails,
  addRtReportModel,
  updateRtReportModel,
  deleteRtReportModel,
  uploadFileLocalReportModel,
  normalizePageResponse
} from '@/api/reportModel'
// 获取模板文件列表（用于关联模板下拉选项）
import { getRtReportModelFileList } from '@/api/api'
import FilePreview from '@/components/FilePreview.vue'

// 仅选择文件，不执行实际上传（提交时以文件流形式携带到新增/编辑接口）
function selectFileOnly (rawFile) {
  return Promise.resolve({
    data: {
      name: rawFile.name,
      url: '',
      size: rawFile.size,
      extension: rawFile.name.split('.').pop().toLowerCase(),
      mime_type: rawFile.type,
      type: 'document'
    }
  })
}
import AjFormAdd from '@/components/ajForm/ajFormAdd.vue'
import AjFormEdit from '@/components/ajForm/ajFormEdit.vue'
import AjTable from '@/components/ajTable/ajTable.vue'
import AjUpload from '@/components/ajUpload/ajUpload.vue'
import EmptyState from '@/components/EmptyState.vue'
import PageHeader from '@/components/PageHeader.vue'
import SearchPanel from '@/components/SearchPanel.vue'
import TablePanel from '@/components/TablePanel.vue'

function createEmptyFormData () {
  return {
    rrmId: null,
    rrmCustomer: '',
    rrmProvince: '',
    rrmCity: '',
    rrmCourseType: '',
    rrmModelName: '',
    rrmModelPath: [],
    rrmAttachment: '',
    rrmSpecial: '',
    // 关联模板文件ID
    relationRrmfIdOne: '',
    relationRrmfIdTwo: '',
    relationRrmfIdThree: ''
  }
}

export default {
  name: 'ReportModelListPage',
  components: { AjFormAdd, AjFormEdit, AjTable, AjUpload, EmptyState, FilePreview, PageHeader, SearchPanel, TablePanel },
  data () {
    return {
      loading: false,
      submitLoading: false,
      list: [],
      total: 0,

      // 搜索
      query: {
        rrmCustomer: '',
        rrmProvince: '',
        rrmCity: '',
        rrmCourseType: '',
        relationRrmfIdOneName: '',
        relationRrmfIdTwoName: '',
        relationRrmfIdThreeName: '',
        currentPage: 1,
        pageSize: 10
      },
      searchCustomerOptions: [],
      searchProvinceOptions: [],
      searchCityOptions: [],
      searchCourseTypeOptions: [],

      // 关联模板文件下拉选项（按类型分组）
      templateOptionsOne: [],   // 考勤成绩
      templateOptionsTwo: [],   // 评估分数
      templateOptionsThree: [], // 总结报告

      // 新增弹窗
      addDialogVisible: false,
      addFormData: createEmptyFormData(),

      // 编辑弹窗
      editDialogVisible: false,
      editFormData: createEmptyFormData(),

      // 查看弹窗
      viewDialogVisible: false,
      viewData: null,
      viewEditingPath: false,
      viewEditFileList: [],
      viewEditRawFile: null,
      viewPathLoading: false,

      // 文件预览
      previewVisible: false,
      previewFileUrl: ''
    }
  },
  computed: {
    columns () {
      return [
        { type: 'index', label: '序号', width: 60 },
        { prop: 'rrmCustomer', label: '客户群体', minWidth: 120 },
        { prop: 'rrmProvince', label: '省份', width: 100 },
        { prop: 'rrmCity', label: '地市', width: 100 },
        { prop: 'rrmCourseType', label: '课程类型', width: 120 },
        { prop: 'rrmModelName', label: '包内含资料', minWidth: 250, showOverflowTooltip: true },
        // 资料地址列：截取文件名展示，同详情页逻辑
        {
          prop: 'rrmModelPath',
          label: '资料地址',
          minWidth: 200,
          render: (h, { row }) => {
            if (!row.rrmModelPath) return h('span', { style: 'color: #b0bec5' }, '-')
            const files = row.rrmModelPath.split(',').filter(Boolean)
            if (!files.length) return h('span', { style: 'color: #b0bec5' }, '-')
            return h('div', { style: 'display: flex; flex-wrap: wrap; gap: 4px;' },
              files.map(url =>
                h('a', {
                  attrs: { href: url, target: '_blank', title: url.split('/').pop() },
                  style: 'display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; background: #f0f5ff; border: 1px solid #dce5f5; border-radius: 4px; color: #3b82f6; font-size: 12px; text-decoration: none; max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;'
                }, [
                  h('i', { class: 'el-icon-document', style: 'flex-shrink: 0; font-size: 12px;' }),
                  url.split('/').pop()
                ])
              )
            )
          }
        },
        {
          prop: 'relationRrmfIdOneName',
          label: '考勤成绩模板',
          width: 140,
          render: (h, { row }) => {
            if (!row.relationRrmfIdOneName) return h('span', { style: 'color: #b0bec5' }, '-')
            return h('el-tooltip', { attrs: { content: row.relationRrmfIdOneName, placement: 'top', openDelay: 300 } }, [
              h('el-tag', { attrs: { size: 'mini' }, style: { maxWidth: '110px', display: 'inline-block', background: '#ecfdf5', borderColor: '#a7f3d0', color: '#059669', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, row.relationRrmfIdOneName)
            ])
          }
        },
        {
          prop: 'relationRrmfIdTwoName',
          label: '评估分数模板',
          width: 140,
          render: (h, { row }) => {
            if (!row.relationRrmfIdTwoName) return h('span', { style: 'color: #b0bec5' }, '-')
            return h('el-tooltip', { attrs: { content: row.relationRrmfIdTwoName, placement: 'top', openDelay: 300 } }, [
              h('el-tag', { attrs: { size: 'mini' }, style: { maxWidth: '110px', display: 'inline-block', background: '#fffbeb', borderColor: '#fde68a', color: '#d97706', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, row.relationRrmfIdTwoName)
            ])
          }
        },
        {
          prop: 'relationRrmfIdThreeName',
          label: '总结报告模板',
          width: 140,
          render: (h, { row }) => {
            if (!row.relationRrmfIdThreeName) return h('span', { style: 'color: #b0bec5' }, '-')
            return h('el-tooltip', { attrs: { content: row.relationRrmfIdThreeName, placement: 'top', openDelay: 300 } }, [
              h('el-tag', { attrs: { size: 'mini' }, style: { maxWidth: '110px', display: 'inline-block', background: '#eff6ff', borderColor: '#bfdbfe', color: '#2563eb', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, row.relationRrmfIdThreeName)
            ])
          }
        },
        { prop: 'rrmSpecial', label: '材料特殊性备注', minWidth: 200, showOverflowTooltip: true },
        { prop: 'createusername', label: '创建人', width: 100 },
        { prop: 'createtime', label: '创建时间', width: 160 },
        {
          type: 'operation',
          prop: 'operation',
          label: '操作',
          width: 200,
          fixed: 'right',
          buttons: [
            { key: 'view', text: '查看', type: 'text' },
            { key: 'edit', text: '编辑', type: 'text' },
            { key: 'delete', text: '删除', type: 'text', class: 'text-danger' }
          ]
        }
      ]
    },
    addFormConfig () {
      return [
        [
          { label: '客户群体', prop: 'rrmCustomer', type: 'input', span: 12, required: true },
          { label: '省份', prop: 'rrmProvince', type: 'input', span: 12, required: true }
        ],
        [
          { label: '地市', prop: 'rrmCity', type: 'input', span: 12 },
          { label: '课程类型', prop: 'rrmCourseType', type: 'input', span: 12, required: true }
        ],
        [
          { label: '包内含资料', prop: 'rrmModelName', type: 'input', span: 24, required: true }
        ],
        [
          { label: '资料地址', prop: 'rrmModelPath', type: 'upload', span: 24, required: true, multiple: false, limit: 1, acceptType: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar', uploadRequest: selectFileOnly, uploadBtnText: '选择资料文件' }
        ],
        [
          { label: '考勤成绩模板', prop: 'relationRrmfIdOne', type: 'select', span: 8, filterable: true, options: this.templateOptionsOne },
          { label: '评估分数模板', prop: 'relationRrmfIdTwo', type: 'select', span: 8, filterable: true, options: this.templateOptionsTwo },
          { label: '总结报告模板', prop: 'relationRrmfIdThree', type: 'select', span: 8, filterable: true, options: this.templateOptionsThree }
        ],
        [
          { label: '本地附加', prop: 'rrmAttachment', type: 'textarea', span: 24, rows: 3 }
        ],
        [
          { label: '材料特殊性备注', prop: 'rrmSpecial', type: 'textarea', span: 24, rows: 3 }
        ]
      ]
    },
    editFormConfig () {
      return [
        [
          { label: '客户群体', prop: 'rrmCustomer', type: 'input', span: 12, required: true },
          { label: '省份', prop: 'rrmProvince', type: 'input', span: 12, required: true }
        ],
        [
          { label: '地市', prop: 'rrmCity', type: 'input', span: 12 },
          { label: '课程类型', prop: 'rrmCourseType', type: 'input', span: 12, required: true }
        ],
        [
          { label: '包内含资料', prop: 'rrmModelName', type: 'input', span: 24, required: true }
        ],
        [
          { label: '资料地址', prop: 'rrmModelPath', type: 'upload', span: 24, required: true, multiple: false, limit: 1, acceptType: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar', uploadRequest: selectFileOnly, uploadBtnText: '选择资料文件', disabled: (formData) => Array.isArray(formData.rrmModelPath) && formData.rrmModelPath.some(f => !f.raw) }
        ],
        [
          { label: '考勤成绩模板', prop: 'relationRrmfIdOne', type: 'select', span: 8, filterable: true, options: this.templateOptionsOne },
          { label: '评估分数模板', prop: 'relationRrmfIdTwo', type: 'select', span: 8, filterable: true, options: this.templateOptionsTwo },
          { label: '总结报告模板', prop: 'relationRrmfIdThree', type: 'select', span: 8, filterable: true, options: this.templateOptionsThree }
        ],
        [
          { label: '本地附加', prop: 'rrmAttachment', type: 'textarea', span: 24, rows: 3 }
        ],
        [
          { label: '材料特殊性备注', prop: 'rrmSpecial', type: 'textarea', span: 24, rows: 3 }
        ]
      ]
    }
  },
  created () {
    this.fetchList()
    this.loadSearchCustomerOptions()
    this.loadTemplateOptions()
  },
  methods: {
    // ============ 列表 ============
    async fetchList () {
      this.loading = true
      try {
        const params = {}
        if (this.query.rrmCustomer) params.rrmCustomer = this.query.rrmCustomer
        if (this.query.rrmProvince) params.rrmProvince = this.query.rrmProvince
        if (this.query.rrmCity) params.rrmCity = this.query.rrmCity
        if (this.query.rrmCourseType) params.rrmCourseType = this.query.rrmCourseType
        if (this.query.relationRrmfIdOneName) params.relationRrmfIdOneName = this.query.relationRrmfIdOneName
        if (this.query.relationRrmfIdTwoName) params.relationRrmfIdTwoName = this.query.relationRrmfIdTwoName
        if (this.query.relationRrmfIdThreeName) params.relationRrmfIdThreeName = this.query.relationRrmfIdThreeName
        params.currentPage = this.query.currentPage
        params.pageSize = this.query.pageSize
        const res = await getRtReportModelList(params)
        const pageData = normalizePageResponse(res)
        this.list = pageData.list
        this.total = pageData.total
      } catch (error) {
        this.list = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    handleSearch () {
      this.query.currentPage = 1
      this.fetchList()
    },
    resetQuery () {
      this.query = { rrmCustomer: '', rrmProvince: '', rrmCity: '', rrmCourseType: '', relationRrmfIdOneName: '', relationRrmfIdTwoName: '', relationRrmfIdThreeName: '', currentPage: 1, pageSize: 10 }
      this.searchProvinceOptions = []
      this.searchCityOptions = []
      this.searchCourseTypeOptions = []
      this.fetchList()
    },
    handlePageChange ({ currentPage, pageSize }) {
      this.query.currentPage = currentPage
      this.query.pageSize = pageSize
      this.fetchList()
    },

    // ============ 搜索面板联动 ============
    // 按类型加载模板文件下拉选项
    async loadTemplateOptions () {
      try {
        const [resOne, resTwo, resThree] = await Promise.all([
          getRtReportModelFileList({ rrmfType: 1, pageSize: 999 }),
          getRtReportModelFileList({ rrmfType: 2, pageSize: 999 }),
          getRtReportModelFileList({ rrmfType: 3, pageSize: 999 })
        ])
        this.templateOptionsOne = (resOne.data || []).map(item => ({ label: item.rrmfName, value: item.rrmfId }))
        this.templateOptionsTwo = (resTwo.data || []).map(item => ({ label: item.rrmfName, value: item.rrmfId }))
        this.templateOptionsThree = (resThree.data || []).map(item => ({ label: item.rrmfName, value: item.rrmfId }))
      } catch (e) {
        this.templateOptionsOne = []
        this.templateOptionsTwo = []
        this.templateOptionsThree = []
      }
    },
    async loadSearchCustomerOptions () {
      try {
        const res = await getRtReportModelListParam({ modelType: 1 })
        this.searchCustomerOptions = res.data || []
      } catch (e) {
        this.searchCustomerOptions = []
      }
    },
    async handleSearchCustomerChange (val) {
      this.query.rrmProvince = ''
      this.query.rrmCity = ''
      this.query.rrmCourseType = ''
      this.searchProvinceOptions = []
      this.searchCityOptions = []
      this.searchCourseTypeOptions = []
      if (!val) return
      try {
        const res = await getRtReportModelListParam({ modelType: 2, rrmCustomer: val })
        this.searchProvinceOptions = res.data || []
      } catch (e) {}
    },
    async handleSearchProvinceChange (val) {
      this.query.rrmCity = ''
      this.query.rrmCourseType = ''
      this.searchCityOptions = []
      this.searchCourseTypeOptions = []
      if (!val) return
      try {
        const res = await getRtReportModelListParam({ modelType: 3, rrmCustomer: this.query.rrmCustomer, rrmProvince: val })
        this.searchCityOptions = res.data || []
      } catch (e) {}
    },
    async handleSearchCityChange (val) {
      this.query.rrmCourseType = ''
      this.searchCourseTypeOptions = []
      if (!val) return
      try {
        const res = await getRtReportModelListParam({ modelType: 4, rrmCustomer: this.query.rrmCustomer, rrmProvince: this.query.rrmProvince, rrmCity: val })
        this.searchCourseTypeOptions = res.data || []
      } catch (e) {}
    },

    // ============ 新增 ============
    async handleAdd () {
      this.addFormData = createEmptyFormData()
      this.addDialogVisible = true
    },
    async handleAddSubmit ({ data }) {
      this.submitLoading = true
      try {
        const fd = new FormData()
        fd.append('rrmCustomer', data.rrmCustomer)
        fd.append('rrmProvince', data.rrmProvince)
        fd.append('rrmCourseType', data.rrmCourseType)
        fd.append('rrmModelName', data.rrmModelName)
        if (data.rrmSpecial) fd.append('rrmSpecial', data.rrmSpecial)
        if (data.rrmCity) fd.append('rrmCity', data.rrmCity)
        if (data.rrmAttachment) fd.append('rrmAttachment', data.rrmAttachment)
        // 关联模板文件ID
        if (data.relationRrmfIdOne) fd.append('relationRrmfIdOne', data.relationRrmfIdOne)
        if (data.relationRrmfIdTwo) fd.append('relationRrmfIdTwo', data.relationRrmfIdTwo)
        if (data.relationRrmfIdThree) fd.append('relationRrmfIdThree', data.relationRrmfIdThree)
        // 文件流：从 ajUpload 文件列表中提取原始 File 对象
        if (Array.isArray(data.rrmModelPath) && data.rrmModelPath.length) {
          data.rrmModelPath.forEach(f => {
            if (f.raw) fd.append('file', f.raw)
          })
        }
        await addRtReportModel(fd)
        this.$message.success('报告包创建成功')
        this.addDialogVisible = false
        this.fetchList()
      } finally {
        this.submitLoading = false
      }
    },

    // ============ 编辑 ============
    async handleEdit (row) {
      try {
        const res = await getRtReportModelDetails({ rrmId: row.rrmId })
        const detail = res.data || row
        this.editFormData = {
          rrmId: detail.rrmId,
          rrmCustomer: detail.rrmCustomer || '',
          rrmProvince: detail.rrmProvince || '',
          rrmCity: detail.rrmCity || '',
          rrmCourseType: detail.rrmCourseType || '',
          rrmModelName: detail.rrmModelName || '',
          rrmModelPath: detail.rrmModelPath ? detail.rrmModelPath.split(',').map(url => ({ name: url.split('/').pop(), url })) : [],
          rrmAttachment: detail.rrmAttachment || '',
          rrmSpecial: detail.rrmSpecial || '',
          // 关联模板文件ID
          relationRrmfIdOne: detail.relationRrmfIdOne || '',
          relationRrmfIdTwo: detail.relationRrmfIdTwo || '',
          relationRrmfIdThree: detail.relationRrmfIdThree || ''
        }
        this.editDialogVisible = true
      } catch (error) {
        this.$message.error('获取报告包详情失败')
      }
    },
    async handleEditSubmit ({ data }) {
      this.submitLoading = true
      try {
        const fd = new FormData()
        fd.append('rrmId', data.rrmId)
        fd.append('rrmCustomer', data.rrmCustomer)
        fd.append('rrmProvince', data.rrmProvince)
        fd.append('rrmCourseType', data.rrmCourseType)
        fd.append('rrmModelName', data.rrmModelName)
        if (data.rrmSpecial) fd.append('rrmSpecial', data.rrmSpecial)
        if (data.rrmCity) fd.append('rrmCity', data.rrmCity)
        if (data.rrmAttachment) fd.append('rrmAttachment', data.rrmAttachment)
        // 关联模板文件ID
        if (data.relationRrmfIdOne) fd.append('relationRrmfIdOne', data.relationRrmfIdOne)
        if (data.relationRrmfIdTwo) fd.append('relationRrmfIdTwo', data.relationRrmfIdTwo)
        if (data.relationRrmfIdThree) fd.append('relationRrmfIdThree', data.relationRrmfIdThree)
        // 携带新上传的文件流
        if (Array.isArray(data.rrmModelPath) && data.rrmModelPath.length) {
          data.rrmModelPath.forEach(f => {
            if (f.raw) fd.append('file', f.raw)
          })
        }
        await updateRtReportModel(fd)
        this.$message.success('报告包更新成功')
        this.editDialogVisible = false
        this.fetchList()
      } finally {
        this.submitLoading = false
      }
    },

    // ============ 查看 ============
    async handleView (row) {
      this.viewEditingPath = false
      this.viewEditFiles = []
      try {
        const res = await getRtReportModelDetails({ rrmId: row.rrmId })
        this.viewData = res.data || row
        this.viewDialogVisible = true
      } catch (error) {
        this.$message.error('获取报告包详情失败')
      }
    },
    handleEditModelPath () {
      this.viewEditFileList = []
      this.viewEditRawFile = null
      this.viewEditingPath = true
    },
    handlePathFileChange (file) {
      if (file && file.raw) {
        this.viewEditRawFile = file.raw
      }
    },
    handlePathFileRemove () {
      this.viewEditRawFile = null
      this.viewEditFileList = []
    },
    async handleSaveModelPath () {
      if (!this.viewEditRawFile) {
        this.$message.warning('请选择一个资料文件')
        return
      }
      this.viewPathLoading = true
      try {
        const fd = new FormData()
        fd.append('rrmId', this.viewData.rrmId)
        fd.append('file', this.viewEditRawFile)
        await uploadFileLocalReportModel(fd)
        this.$message.success('资料地址更新成功')
        this.viewEditingPath = false
        const res = await getRtReportModelDetails({ rrmId: this.viewData.rrmId })
        this.viewData = res.data || this.viewData
        this.fetchList()
      } catch (error) {
        // 错误已由拦截器处理
      } finally {
        this.viewPathLoading = false
      }
    },

    // ============ 删除 ============
    async handleDelete (row) {
      try {
        await this.$confirm('确认删除该报告包吗？', '提示', { type: 'warning' })
        await deleteRtReportModel({ rrmId: row.rrmId })
        this.$message.success('报告包已删除')
        this.fetchList()
      } catch (error) {}
    },

    // ============ 操作按钮分发 ============
    handleOperateBtnClick ({ btn, row }) {
      switch (btn.key) {
        case 'view': this.handleView(row); break
        case 'edit': this.handleEdit(row); break
        case 'delete': this.handleDelete(row); break
      }
    },

    // ============ 文件预览 ============
    openFilePreview (url) {
      this.previewFileUrl = url
      this.previewVisible = true
    },
    // 下载资料文件到电脑（<a> 标签下载，不走 fetch，避免跨域）
    downloadToLocal (url) {
      if (!url) return
      const a = document.createElement('a')
      a.href = url
      a.target = '_blank'
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      this.$message({ type: 'success', message: '文件已开始下载，可在浏览器下载栏（Ctrl+J）查看', duration: 5000 })
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  background: #fff;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid #d1d5db;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.text-danger { color: #ef4444 !important; }

/* ========== 详情弹框 ========== */
::v-deep .detail-dialog {
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 16px 48px rgba(15, 23, 42, 0.12);

  .el-dialog__header {
    padding: 0;
    border-bottom: none;
  }

  .el-dialog__body {
    padding: 0;
    max-height: 70vh;
    overflow-x: hidden;
    overflow-y: auto;

    &::-webkit-scrollbar { width: 5px; }
    &::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 5px; }
    &::-webkit-scrollbar-track { background: transparent; }
  }

  .el-dialog__footer {
    padding: 14px 28px;
    border-top: 1px solid #eef1f6;
  }
}

.detail-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 28px 18px;
  border-bottom: 1px solid #eef1f6;
}

.detail-dialog-title {
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
}


.detail-content {
  padding: 20px 28px 24px;
}

// 分区标题
.detail-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
  padding-left: 10px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 16px;
    background: linear-gradient(180deg, #4f7cff, #6366f1);
    border-radius: 2px;
  }

  i {
    font-size: 16px;
    color: #4f7cff;
    margin-left: 4px;
  }

  & + .detail-table {
    margin-bottom: 24px;
  }
}

// 表格布局：标签灰底 + 值白底
.detail-table {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 24px;

  &--2col {
    .detail-row {
      display: grid;
      grid-template-columns: repeat(2, 120px 1fr);
    }
  }
}

.detail-row {
  display: flex;
  border-bottom: 1px solid #e2e8f0;

  &:last-child {
    border-bottom: none;
  }
}

.detail-cell {
  padding: 12px 16px;
  font-size: 13px;
  line-height: 1.6;

  &--label {
    background: #f5f7fa;
    color: #606266;
    font-weight: 500;
    white-space: nowrap;
    width: 120px;
    flex-shrink: 0;
    border-right: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
  }

  &--value {
    background: #fff;
    color: #1e293b;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    word-break: break-all;

    &.detail-cell--pre {
      white-space: pre-wrap;
      color: #475569;
    }
  }
}

// 模板备注与图片
.template-des {
  margin-top: 6px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.6;
}

.template-img-wrap {
  margin-top: 8px;
}

// 资料地址展示
.detail-path-show {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.detail-file-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex: 1;
}

.detail-file-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  background: #f0f5ff;
  border: 1px solid #dce5f5;
  border-radius: 6px;
  color: #3b82f6;
  font-size: 12px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.15s;
  max-width: 220px;

  i { flex-shrink: 0; font-size: 13px; }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    background: #e0ecff;
    color: #2563eb;
    text-decoration: none;
  }
}

.detail-path-trigger {
  flex-shrink: 0;
  color: #a0aec0;
  font-size: 13px;
  &:hover { color: #4f7cff; }
}

.detail-path-edit {
  width: 100%;
  max-width: 100%;
  overflow: hidden;

  // el-upload 文件列表不溢出
  ::v-deep .el-upload {
    display: block;
  }

  ::v-deep .el-upload-list {
    display: block;
    max-width: 100%;
    overflow: hidden;

    .el-upload-list__item {
      max-width: 100%;
      box-sizing: border-box;

      .el-upload-list__item-name {
        display: block;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }
}

.detail-path-actions {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px dashed #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.detail-empty {
  color: #b0bec5;
  font-size: 13px;
  font-style: italic;
  line-height: 28px;
}

// 底部元信息
.detail-meta {
  display: flex;
  gap: 28px;
  padding: 14px 0 0;
  border-top: 1px solid #eef1f6;
}

.detail-meta__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #a0aec0;

  i { font-size: 13px; }
}

// 操作链接
::v-deep .aj-table-operation-link {
  margin: 0 4px;
  font-size: 12px;
  font-weight: 500;

  &.text-danger {
    color: #ef4444 !important;
    &:hover { color: #dc2626 !important; }
  }

  &:not(.text-danger):hover { color: #4f7cff !important; }
}

// 详情页文件预览入口样式
.detail-file-preview {
  cursor: pointer;

  &:hover {
    background: #dbeafe;
    border-color: #93c5fd;
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(59, 130, 246, 0.15);
  }
}

// 资料地址保存到电脑按钮
.detail-download-btn {
  color: #10b981;
  padding: 5px 8px;
  &:hover { color: #059669; }
}
</style>
